import React, { useState, useEffect } from 'react';
import { BusinessCard } from '../types';

interface ImagePopupProps {
  card: BusinessCard | null;
  isOpen: boolean;
  onClose: () => void;
}

const ImagePopup: React.FC<ImagePopupProps> = ({ card, isOpen, onClose }) => {
  const [currentView, setCurrentView] = useState<'front' | 'back'>('front');

  useEffect(() => {
    if (isOpen) {
      setCurrentView('front');
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !card) return null;

  const frontImage = card.image_path ? `/media/${card.image_path}` : null;
  const backImage = card.image_back_path ? `/media/${card.image_back_path}` : null;
  const currentImage = currentView === 'front' ? frontImage : backImage;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          maxWidth: '95vw',
          maxHeight: '95vh',
          overflow: 'auto',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          borderBottom: '1px solid #eee',
          paddingBottom: '15px'
        }}>
          <div>
            <h3 style={{ margin: '0 0 5px 0', fontSize: '18px', color: '#333' }}>
              {card.full_name}{card.full_name_reading && `（${card.full_name_reading}）`}の名刺
            </h3>
            {card.company && (
              <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                {card.company}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '5px',
              lineHeight: '1'
            }}
          >
            ×
          </button>
        </div>

        {/* 表裏切り替えボタン */}
        {(frontImage || backImage) && (
          <div style={{
            display: 'flex',
            marginBottom: '20px',
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid #ddd'
          }}>
            <button
              onClick={() => setCurrentView('front')}
              style={{
                flex: 1,
                padding: '10px 20px',
                backgroundColor: currentView === 'front' ? '#007bff' : '#f8f9fa',
                color: currentView === 'front' ? 'white' : '#333',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'all 0.2s'
              }}
            >
              表面 {frontImage ? '' : '(なし)'}
            </button>
            <button
              onClick={() => setCurrentView('back')}
              style={{
                flex: 1,
                padding: '10px 20px',
                backgroundColor: currentView === 'back' ? '#007bff' : '#f8f9fa',
                color: currentView === 'back' ? 'white' : '#333',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'all 0.2s'
              }}
            >
              裏面 {backImage ? '' : '(なし)'}
            </button>
          </div>
        )}

        {/* 画像表示エリア */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '20px',
          height: '450px',
          overflow: 'hidden',
          alignItems: 'center'
        }}>
          {currentImage ? (
            <img
              src={currentImage}
              alt={`${card.full_name}の名刺（${currentView === 'front' ? '表面' : '裏面'}）`}
              style={{
                maxWidth: '400px',
                maxHeight: '600px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                objectFit: 'contain',
                transform: 'rotate(90deg)'
              }}
            />
          ) : (
            <div style={{
              width: '400px',
              height: '240px',
              backgroundColor: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              border: '2px dashed #ddd',
              color: '#999',
              fontSize: '16px'
            }}>
              {currentView === 'front' ? '表面画像なし' : '裏面画像なし'}
            </div>
          )}
        </div>

        {/* 名刺情報 */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '15px',
          borderRadius: '8px',
          fontSize: '14px'
        }}>
          <div style={{ marginBottom: '8px' }}>
            <strong>氏名:</strong> {card.full_name}{card.full_name_reading && `（${card.full_name_reading}）`}
          </div>
          {card.company && (
            <div style={{ marginBottom: '8px' }}>
              <strong>会社:</strong> {card.company}
            </div>
          )}
          {card.title && (
            <div style={{ marginBottom: '8px' }}>
              <strong>役職:</strong> {card.title}
            </div>
          )}
          {card.email && (
            <div style={{ marginBottom: '8px' }}>
              <strong>Email:</strong> {card.email}
            </div>
          )}
          {card.phone && (
            <div style={{ marginBottom: '8px' }}>
              <strong>電話:</strong> {card.phone}
            </div>
          )}
          {card.address && (
            <div style={{ marginBottom: '8px' }}>
              <strong>住所:</strong> {card.address}
            </div>
          )}
          {card.memo && (
            <div style={{ marginBottom: '8px' }}>
              <strong>メモ:</strong> {card.memo}
            </div>
          )}
          {card.tags && card.tags.length > 0 && (
            <div>
              <strong>タグ:</strong>{' '}
              {card.tags.map((tag, index) => (
                <span
                  key={index}
                  style={{
                    display: 'inline-block',
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    padding: '2px 6px',
                    borderRadius: '10px',
                    fontSize: '12px',
                    marginLeft: index === 0 ? '5px' : '3px'
                  }}
                >
                  {typeof tag === 'string' ? tag : (tag as any).name}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* 操作ボタン */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '20px',
          gap: '10px'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImagePopup;